import React, { useEffect, useState, useRef } from "react";
import cta from "./Assets/cta-image.svg";

import { Drawer, Button, Grid, Carousel, List, Spin, Input, Modal, message } from "antd";

import {
  RightOutlined,
  MenuOutlined,
  CloseOutlined,
  LeftOutlined,
  EnvironmentTwoTone,
  PhoneTwoTone,
  MailTwoTone,
  WhatsAppOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  SearchOutlined,
  PlayCircleOutlined,
  FacebookOutlined,
  LinkedinOutlined,
  TwitterOutlined,
  YoutubeOutlined,
  StarFilled,
} from "@ant-design/icons";

// Reveal
import AOS from "aos";
import "aos/dist/aos.css";

import "./styles.scss";
import "./button.scss";

import logo from "../../Assets/Logos/ibas_logo.jpeg";
import { getHomeCourses, submitInquiry, getAllTestimonials, getAllAcademicPanelMembers } from "../../Constants/Api/functions/function";
import {
  DEFAULT_COURSE_COVER_IMAGE,
  DEFAULT_USER_PROFILE_IMAGE,
} from "../../Constants/Api/azure/azure";
import { Link } from "react-router-dom";

const PromoCountdown = ({ endDate }) => {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    if (!endDate) return;
    
    const getTargetDate = () => {
      const target = new Date(endDate);
      const str = String(endDate);
      if (str.length <= 10 || (target.getUTCHours() === 0 && target.getUTCMinutes() === 0 && target.getUTCSeconds() === 0)) {
        target.setHours(23, 59, 59, 999);
      }
      return target;
    };

    const calculateTimeLeft = () => {
      const difference = +getTargetDate() - +new Date();
      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((difference / 1000 / 60) % 60);
        const seconds = Math.floor((difference / 1000) % 60);

        if (days > 0) {
          return `Ends in ${days}d ${hours}h ${minutes}m`;
        }
        return `Ends in ${hours}h ${minutes}m ${seconds}s`;
      }
      return null;
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);
      if (!remaining) clearInterval(timer);
    }, 1000);

    return () => clearInterval(timer);
  }, [endDate]);

  if (!timeLeft) return null;

  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 8px',
      background: '#fff2f0',
      border: '1px solid #ffccc7',
      borderRadius: '4px',
      color: '#ff4d4f',
      fontSize: '11px',
      fontWeight: 'bold',
      marginTop: '4px',
      whiteSpace: 'nowrap'
    }}>
      <ClockCircleOutlined style={{ marginRight: '4px' }} />
      {timeLeft}
    </div>
  );
};

// Standard currency formatter with comma separation, e.g. "10,000 LKR"
const formatLKR = (amount) => {
  if (amount === undefined || amount === null || amount === "") return "";
  const num = Number(amount);
  if (isNaN(num)) return `${amount} LKR`;
  return `${Math.round(num).toLocaleString("en-US")} LKR`;
};

// Course time period / duration helper
const getCourseDuration = (item) => {
  if (item?.duration && typeof item.duration === "string" && item.duration.trim()) {
    return item.duration.trim();
  }
  if (item?.description && typeof item.description === "string") {
    const match = item.description.match(/(?:Duration|⏳\s*Duration)\s*:\s*([^<\n\r]+)/i);
    if (match && match[1]) {
      const clean = match[1].replace(/<\/?[^>]+(>|$)/g, "").trim();
      if (clean) return clean;
    }
    const periodMatch = item.description.match(/(\d+(?:\.\d+)?\s*(?:Months?|Weeks?|Days?|Hours?))/i);
    if (periodMatch && periodMatch[1]) {
      return periodMatch[1].trim();
    }
  }
  return "Flexible Duration";
};

// Check if course is currently within an active promotional period
const isCourseInPromoPeriod = (course) => {
  const numPrice = Number(course?.price);
  const numPromo = Number(course?.promoPrice);

  if (isNaN(numPromo) || numPromo <= 0 || isNaN(numPrice) || numPromo >= numPrice) {
    return false;
  }

  // Promotional pricing must be active
  const isPromoFlagActive = course?.isPromoActive === 1 || course?.isPromoActive === true || course?.isPromoActive === "1";
  if (!isPromoFlagActive) {
    return false;
  }

  // Must have a promo end date that has not elapsed
  if (!course?.promoEndDate) {
    return false;
  }

  const now = new Date();
  const end = new Date(course.promoEndDate);
  const str = String(course.promoEndDate);
  if (str.length <= 10 || (end.getUTCHours() === 0 && end.getUTCMinutes() === 0 && end.getUTCSeconds() === 0)) {
    end.setHours(23, 59, 59, 999);
  }

  if (now > end) {
    return false;
  }

  if (course?.promoStartDate) {
    const start = new Date(course.promoStartDate);
    if (now < start) {
      return false;
    }
  }

  return true;
};

// Standard specifications for Why Choose IBAS (matching all 8 requirements)
const specifications = [
  {
    title: "Government Registered Institute",
    description: "Government Registered Higher Education Institute under PV No: 00264413, ensuring certified legitimacy.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Recognized Programs",
    description: "Accredited and recognized by the Association of Accounting Technicians of Sri Lanka (AAT) and other key institutions.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Industry Expert Lecturers",
    description: "Learn from Senior Chartered Accountants, University Professors, Professional Managers, Legal Experts, and Academic Administrators.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="9" cy="7" r="4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Flexible Learning Options",
    description: "Offering the best of hybrid learning, allowing you to choose between physical classroom learning and fully online classes.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" strokeWidth="2" />
        <line x1="8" y1="21" x2="16" y2="21" strokeWidth="2" />
        <line x1="12" y1="17" x2="12" y2="21" strokeWidth="2" />
      </svg>
    ),
  },
  {
    title: "Online LMS Access",
    description: "Get lifetime access to study materials, class recordings, live webinar feeds, and quizzes through our modern LMS.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Career Focused Education",
    description: "Our practical skill curricula are tailored strictly around employment standards and professional market demands.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" strokeWidth="2" />
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" strokeWidth="2" />
      </svg>
    ),
  },
  {
    title: "Practical Skill Development",
    description: "Direct hands-on experience using computer systems, industry tools, dashboards, and live-project templates.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" strokeWidth="2" />
      </svg>
    ),
  },
  {
    title: "Affordable Fees",
    description: "Get international quality corporate learning, flexible installment payments, and affordable course structures.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <rect x="2" y="5" width="20" height="14" rx="2" strokeWidth="2" />
        <line x1="2" y1="10" x2="22" y2="10" strokeWidth="2" />
      </svg>
    ),
  },
];

const statsData = [
  {
    label: "Active Students",
    value: "2,500+",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="9" cy="7" r="4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: "Industry Certifications",
    value: "15+",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    label: "Professional Courses",
    value: "50+",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M12 7v14" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: "Student Satisfaction",
    value: "98%",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

const features = [
  "Government Registered higher education standards",
  "Senior Chartered Accountants & Expert Faculty",
  "Lifetime LMS access with recorded webinars",
  "Direct WhatsApp support and career alignment",
];

const testimonials = [
  {
    id: 1,
    content: "The QuickBooks and taxation diploma program at IBAS was outstanding. The lecturer, a Senior Chartered Accountant, gave us actual company files to audit. Within a month after graduating, I secured a Senior Accountant job in Gampaha!",
    author: "Dinuka Hasaranga",
    course: "Diploma in Accounting & QuickBooks",
    rating: 5,
    image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
  },
  {
    id: 2,
    content: "IBAS completely redefined flexible online learning. Since I am employed full-time in Colombo, attending physical classes was impossible. Their LMS portal let me access recorded webinars and download Excel dashboards late at night. Extremely convenient!",
    author: "Shalani Wijewardena",
    course: "Advanced Excel & Data Analytics",
    rating: 5,
    image: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
  },
  {
    id: 3,
    content: "Our AAT examination panel at IBAS was excellent. They literally mapped out exactly how to structure calculations and answers. The support was top-tier, answering questions via WhatsApp at any hour. Proudly qualified AAT on my first attempt!",
    author: "Mineth Rajapaksha",
    course: "AAT Professional Exam Classes",
    rating: 5,
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
  },
  {
    id: 4,
    content: "The ChatGPT and Prompt Engineering course was a massive game-changer. I learned how to automate marketing copy and manage client projects in half the time. Truly cutting-edge curriculum that is rarely found in other Sri Lankan institutes.",
    author: "Dilani Alwis",
    course: "AI for Business & ChatGPT",
    rating: 5,
    image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
  },
];

// Exact Course categories matching 100% of specifications
const courseCategories = [
  {
    id: "management",
    name: "Management",
    icon: (
      <svg className="category-icon-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    description: "Develop operational excellence, robust business management workflows, human resource practices, and startup launch metrics.",
    subCourses: [
      "Business Management",
      "Human Resource Management",
      "Entrepreneurship",
      "Project Management",
      "Office Administration"
    ]
  },
  {
    id: "accounting",
    name: "Accounting & Finance",
    icon: (
      <svg className="category-icon-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    description: "Master financial accounting standards, tax codes, audit compliance, QuickBooks software, and cash flow governance.",
    subCourses: [
      "Diploma in Accounting",
      "QuickBooks Accounting",
      "Computerized Accounting",
      "Taxation",
      "Auditing",
      "Financial Management"
    ]
  },
  {
    id: "data-it",
    name: "Data Analytics & IT",
    icon: (
      <svg className="category-icon-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 002 2h2a2 2 0 002-2z" />
      </svg>
    ),
    description: "Unlock business intelligence, compile data models in Power BI, design custom dashboard reports, and execute advanced Excel algorithms.",
    subCourses: [
      "Advanced Excel",
      "Power BI",
      "Data Analytics",
      "Dashboard Reporting",
      "Business Intelligence"
    ]
  },
  {
    id: "ai",
    name: "Artificial Intelligence (AI)",
    icon: (
      <svg className="category-icon-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    description: "Accelerate productivity with modern AI models. Develop expert prompt engineering skills and automate tasks using ChatGPT.",
    subCourses: [
      "AI for Business",
      "ChatGPT & AI Tools",
      "Prompt Engineering",
      "AI Productivity",
      "AI for Marketing"
    ]
  },
  {
    id: "logistics",
    name: "Logistics & Supply Chain",
    icon: (
      <svg className="category-icon-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
      </svg>
    ),
    description: "Govern global warehouse operations, container shipping routing, vendor procurement matrices, and supply chains.",
    subCourses: [
      "Logistics Management",
      "Warehouse Management",
      "Shipping Management",
      "Procurement Management"
    ]
  },
  {
    id: "design",
    name: "Graphic Design & Multimedia",
    icon: (
      <svg className="category-icon-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    description: "Acquire mastery over Illustrator path alignments, Photoshop compositing, Canva dashboard features, and high-impact video editing.",
    subCourses: [
      "Graphic Design",
      "Photoshop",
      "Illustrator",
      "Canva Design",
      "Video Editing"
    ]
  },
  {
    id: "architecture",
    name: "Architecture & Design",
    icon: (
      <svg className="category-icon-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
    description: "Draft high-fidelity architectural models, detailed AutoCAD orthographics, interior visualization renders, and 3D modeling.",
    subCourses: [
      "AutoCAD",
      "Architectural Design",
      "Interior Design",
      "3D Visualization"
    ]
  },
  {
    id: "professional-exams",
    name: "Professional Exam Classes",
    icon: (
      <svg className="category-icon-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
      </svg>
    ),
    description: "Accelerate your success in core certified exams. Comprehensive support for AAT Sri Lanka, Chartered Accounting, and SLIM classes.",
    subCourses: [
      "Association of Accounting Technicians of Sri Lanka (AAT)",
      "Chartered Accounting",
      "Sri Lanka Institute of Marketing (SLIM)",
      "Professional Business Qualifications"
    ]
  }
];

const academicPanel = [
  {
    title: "Senior Chartered Accountants",
    desc: "Overseeing all accounting, tax, auditing, and corporate governance curricula to ensure direct industry alignment.",
    icon: "💼"
  },
  {
    title: "University Professors",
    desc: "Structuring academic methodologies, analytical concepts, research benchmarks, and business theory models.",
    icon: "🎓"
  },
  {
    title: "Professional Managers",
    desc: "Delivering real-world management case studies, leadership patterns, human resource practices, and logistics workflows.",
    icon: "🤝"
  },
  {
    title: "Legal Experts",
    desc: "Covering taxation codes, business formation laws, statutory regulations, and Sri Lankan employment legislature.",
    icon: "⚖️"
  },
  {
    title: "Industry Specialists",
    desc: "Leading practical workshops on advanced Excel models, Power BI reporting, and custom dashboard development.",
    icon: "🚀"
  },
  {
    title: "Academic Administrators",
    desc: "Managing student enrollment schedules, webinar integrations, remote exam boards, and LMS software stability.",
    icon: "🖥️"
  }
];

const Landing = () => {
  const carouselRef = useRef(null);
  const [courses, setCourses] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);

  // Search state and category filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("management");
  const [expandedCategory, setExpandedCategory] = useState(null); // mobile accordion
  const [videoModalVisible, setVideoModalVisible] = useState(false);

  // Inquiry Form state
  const [inquiryName, setInquiryName] = useState("");
  const [inquiryEmail, setInquiryEmail] = useState("");
  const [inquiryPhone, setInquiryPhone] = useState("");
  const [inquiryCourse, setInquiryCourse] = useState("");
  const [inquiryMessage, setInquiryMessage] = useState("");

  // Dynamic website content & submissions state
  const [dynamicTestimonials, setDynamicTestimonials] = useState([]);
  const [dynamicAcademicPanel, setDynamicAcademicPanel] = useState([]);
  const [isInquirySubmitting, setIsInquirySubmitting] = useState(false);

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  const showDrawer = () => setDrawerVisible(true);
  const closeDrawer = () => setDrawerVisible(false);

  useEffect(() => {
    AOS.init({
      disable: false,
      startEvent: "DOMContentLoaded",
      initClassName: "aos-init",
      animatedClassName: "aos-animate",
      useClassNames: false,
      disableMutationObserver: false,
      debounceDelay: 50,
      throttleDelay: 99,
      offset: 120,
      delay: 0,
      duration: 500,
      easing: "ease",
      once: false,
      mirror: false,
      anchorPlacement: "top-bottom",
    });

    const timer = setTimeout(() => {
      AOS.refresh();
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (!el) return;

    const header = document.querySelector("header");
    const headerOffset = header ? header.clientHeight + 16 : 0;
    const elementY = el.getBoundingClientRect().top + window.pageYOffset;

    window.scrollTo({
      top: elementY - headerOffset,
      behavior: "smooth",
    });

    if (isMobile) closeDrawer();
  };

  useEffect(() => {
    handleGetHomeCourses();
    handleGetWebsiteContent();
  }, []);

  useEffect(() => {
    if (window.location.hash) {
      const sectionId = window.location.hash.substring(1);
      setTimeout(() => {
        scrollToSection(sectionId);
      }, 500);
    }
  }, []);

  const handleGetWebsiteContent = async () => {
    try {
      const tests = await getAllTestimonials();
      if (tests && tests.length > 0) {
        setDynamicTestimonials(tests);
      }
      const panel = await getAllAcademicPanelMembers();
      if (panel && panel.length > 0) {
        setDynamicAcademicPanel(panel);
      }
    } catch (err) {
      console.error("Error fetching dynamic website content:", err);
    }
  };

  const handleGetHomeCourses = async () => {
    setIsLoading(true);
    try {
      const response = await getHomeCourses();
      if (response && response.length > 0) {
        const sortedResponse = response.sort(
          (a, b) => a?.orderNumber - b?.orderNumber
        );
        setCourses(sortedResponse);
      } else {
        setCourses([]);
      }
    } catch (error) {
      console.error(error);
      setCourses([]); // robust fallback
    }
    setIsLoading(false);
  };

  // Run Course Search & Filter
  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    scrollToSection("courses-section");
  };

  const handleTagClick = (tag) => {
    setSearchQuery(tag);
    scrollToSection("courses-section");
  };

  // Filter courses list dynamically
  const getFilteredCourses = () => {
    if (!courses) return [];

    // Filter by query if query exists
    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase().trim();
      return courses.filter(c => {
        const courseName = c.name.toLowerCase();
        const lecturerName = c.lecturer ? c.lecturer.toLowerCase() : "";
        const creatorName = c.createdBy?.name ? c.createdBy.name.toLowerCase() : "";
        const courseTags = c.tags ? c.tags.toLowerCase() : "";

        // 1. Direct match check
        if (
          courseName.includes(query) ||
          lecturerName.includes(query) ||
          creatorName.includes(query) ||
          courseTags.includes(query)
        ) {
          return true;
        }

        // 2. Intelligent Category Mapping & Keyword intersections
        if (query === "accounting & finance" || query === "accounting" || query === "finance" || query === "taxation") {
          return courseName.includes("accounting") || courseName.includes("quickbooks") || courseName.includes("tax") || courseTags.includes("accounting & finance");
        }
        if (query === "data analytics & it" || query === "data analytics" || query === "it") {
          return courseName.includes("excel") || courseName.includes("power bi") || courseName.includes("analytics") || courseName.includes("data") || courseTags.includes("data analytics & it");
        }
        if (query === "artificial intelligence (ai)" || query.includes("ai") || query.includes("artificial") || query === "chatgpt" || query.includes("prompt")) {
          return courseName.includes("ai") || courseName.includes("intelligence") || courseName.includes("chatgpt") || courseName.includes("prompt") || courseTags.includes("artificial intelligence (ai)");
        }
        if (query === "logistics & supply chain" || query.includes("logistics") || query.includes("supply")) {
          return courseName.includes("logistics") || courseName.includes("supply") || courseName.includes("shipping") || courseTags.includes("logistics & supply chain");
        }
        if (query === "management" || query.includes("management") || query === "business management" || query === "hr") {
          if (query === "management" && courseName.includes("logistics")) return false;
          return courseName.includes("management") || courseName.includes("administration") || courseName.includes("hr") || courseName.includes("business") || courseName.includes("entrepreneurship") || courseTags.includes("management");
        }
        if (query === "professional exam classes" || query.includes("aat") || query.includes("chartered") || query.includes("exam")) {
          return courseName.includes("aat") || courseName.includes("prep") || courseName.includes("exam") || courseName.includes("chartered") || courseTags.includes("professional exam classes");
        }
        if (query === "graphic design & multimedia" || query.includes("graphic") || query.includes("design") || query.includes("multimedia") || query.includes("photoshop")) {
          return courseName.includes("design") || courseName.includes("photoshop") || courseName.includes("illustrator") || courseName.includes("video") || courseTags.includes("graphic design & multimedia");
        }
        if (query === "architecture & design" || query.includes("architecture") || query.includes("autocad")) {
          return courseName.includes("autocad") || courseName.includes("architecture") || courseName.includes("drafting") || courseTags.includes("architecture & design");
        }

        // 3. Sub-word intersections (matches if any word matches)
        const queryWords = query.split(/\s+/).filter(w => w.length > 2 && w !== "and" && w !== "for" && w !== "&");
        if (queryWords.length > 0) {
          return queryWords.some(word => courseName.includes(word) || lecturerName.includes(word) || courseTags.includes(word));
        }

        return false;
      });
    }
    return courses;
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    if (!inquiryName || !inquiryPhone || !inquiryMessage) {
      message.error("Please fill in all required fields (Name, Phone, and Message).");
      return;
    }

    setIsInquirySubmitting(true);
    try {
      const response = await submitInquiry({
        name: inquiryName,
        email: inquiryEmail || "not_provided@ibas.lk",
        phone: inquiryPhone,
        course: inquiryCourse || "General Inquiry",
        message: inquiryMessage
      });

      if (response && response.success) {
        Modal.success({
          title: "Inquiry Successfully Submitted!",
          content: (
            <div>
              <p>Thank you <strong>{inquiryName}</strong> for contacting IBAS Institute.</p>
              <p>An educational counselor will reach out to you at <strong>{inquiryPhone}</strong> shortly regarding the <strong>{inquiryCourse || "selected"}</strong> program.</p>
            </div>
          ),
          okText: "Done",
          onOk() {
            // Reset Form
            setInquiryName("");
            setInquiryEmail("");
            setInquiryPhone("");
            setInquiryCourse("");
            setInquiryMessage("");
          }
        });
      } else {
        message.error(response?.message || "Failed to submit inquiry. Please try again.");
      }
    } catch (error) {
      console.error("Submit inquiry error:", error);
      message.error("An unexpected error occurred. Please try again later.");
    } finally {
      setIsInquirySubmitting(false);
    }
  };

  const renderAcademicPanel = dynamicAcademicPanel.length > 0
    ? dynamicAcademicPanel.map(p => ({
      title: p.name,
      desc: p.description,
      icon: p.imageUrl || p.designation || "🎓"
    }))
    : academicPanel;

  const renderTestimonials = dynamicTestimonials.length > 0
    ? dynamicTestimonials.map((t, idx) => ({
      id: t.id || idx,
      content: t.feedback,
      author: t.name,
      course: t.role,
      rating: t.rating,
      image: t.avatarUrl || "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop"
    }))
    : testimonials;

  const currentCategoryObj = courseCategories.find(cat => cat.id === activeCategory) || courseCategories[0];

  return (
    <div className="landing-page" id="landinghome">
      <header className="site-header">
        <div className="header-conteiner">
          <div className="logo">
            <a href="/" aria-label="IBAS Home">
              <img src={logo} alt="IBAS Logo" />
            </a>
          </div>

          {/* hamburger icon, mobile only */}
          {isMobile && (
            <Button
              type="text"
              className="mobile-menu-icon"
              onClick={showDrawer}
              aria-label="Open Menu"
              icon={<MenuOutlined />}
            />
          )}

          {/* desktop navigation */}
          <div className="btns">
            <Button type="link" onClick={() => scrollToSection("landinghome")}>
              Home
            </Button>
            <Link to="/verify-certificate">
              <Button type="link">Verify Certificate</Button>
            </Link>
            <Button type="link" onClick={() => scrollToSection("course-finder")}>
              Course Finder
            </Button>
            <Button type="link" onClick={() => scrollToSection("courses-section")}>
              Courses
            </Button>
            <Button type="link" onClick={() => scrollToSection("faculties")}>
              Faculties
            </Button>
            <Button type="link" onClick={() => scrollToSection("professional-programs")}>
              Professional Programs
            </Button>
            <Button type="link" onClick={() => scrollToSection("blog-news")}>
              Blog
            </Button>
            <Button type="link" onClick={() => scrollToSection("contact-us")}>
              Contact Us
            </Button>
            <Link className="signin-btn" to="/register">
              Apply Now
            </Link>
            <Link className="signin-btn btn-solid-gold" to="/login">
              LMS Login
            </Link>
          </div>
        </div>

        {/* mobile drawer */}
        {isMobile && (
          <Drawer
            placement="right"
            closable={true}
            onClose={closeDrawer}
            open={drawerVisible}
            closeIcon={<CloseOutlined />}
            className="mobile-menu-drawer"
            width={280}
          >
            <div className="drawer-menu">
              <Button block type="link" onClick={() => scrollToSection("landinghome")}>
                <span>Home</span>
                <RightOutlined />
              </Button>
              <Link to="/verify-certificate">
                <Button block type="link" onClick={closeDrawer}>
                  <span>Verify Certificate</span>
                  <RightOutlined />
                </Button>
              </Link>
              <Button block type="link" onClick={() => scrollToSection("course-finder")}>
                <span>Course Finder</span>
                <RightOutlined />
              </Button>
              <Button block type="link" onClick={() => scrollToSection("courses-section")}>
                <span>Courses</span>
                <RightOutlined />
              </Button>
              <Button block type="link" onClick={() => scrollToSection("faculties")}>
                <span>Faculties</span>
                <RightOutlined />
              </Button>
              <Button block type="link" onClick={() => scrollToSection("professional-programs")}>
                <span>Professional Programs</span>
                <RightOutlined />
              </Button>
              <Button block type="link" onClick={() => scrollToSection("blog-news")}>
                <span>Blog & News</span>
                <RightOutlined />
              </Button>
              <Button block type="link" onClick={() => scrollToSection("contact-us")}>
                <span>Contact Us</span>
                <RightOutlined />
              </Button>
              <Link to="/register">
                <Button block type="link" onClick={closeDrawer}>
                  <span>Apply Now</span>
                  <RightOutlined />
                </Button>
              </Link>
            </div>

            <div className="drawer-footer">
              <Link to="/login">
                <Button block type="primary" size="large" onClick={closeDrawer}>
                  LMS Login
                </Button>
              </Link>
            </div>
          </Drawer>
        )}
      </header>

      <div className="body">
        {/* HERO SECTION */}
        <section
          className="hero animate-fade-in"
          data-aos="fade-up"
          data-aos-duration="900"
        >
          <div className="hero-container">
            <div className="content">

              <h1 className="hero-title">
                Creating the <br />
                <span className="highlight">Challenging Professional</span><br />
                for a Better Working World
              </h1>

              <p className="hero-subtitle">
                Accredited & Recognized by AAT Sri Lanka
              </p>

              <div className="hero-features">
                <span>
                  <CheckCircleOutlined className="check-icon" /> Online & Physical Classes
                </span>
                <span>
                  <CheckCircleOutlined className="check-icon" /> Industry Expert Lecturers
                </span>
                <span>
                  <CheckCircleOutlined className="check-icon" /> Flexible Learning
                </span>
                <span>
                  <CheckCircleOutlined className="check-icon" /> Professional Certifications
                </span>
                <span>
                  <CheckCircleOutlined className="check-icon" /> Career Focused Programs
                </span>
              </div>

              <div className="hero-buttons">
                <Link className="btn btn-primary" to="/register">
                  Apply Now
                </Link>
                <button className="btn btn-secondary" onClick={() => scrollToSection("course-finder")}>
                  Explore Courses
                </button>
                <a className="btn btn-seminar" href="https://wa.me/94712163163?text=I%20am%20interested%20in%20joining%20the%20next%20free%20seminar" target="_blank" rel="noreferrer">
                  <WhatsAppOutlined style={{ marginRight: '6px' }} /> Join Free Seminar
                </a>
              </div>
            </div>

            <div className="hero-right">
              <div className="image-wrapper">
                <img
                  src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&h=600&fit=crop"
                  alt="IBAS Professional Students"
                  className="hero-image"
                />
                <div className="floating-badge">
                  <div className="badge-icon">
                    <UserOutlined />
                  </div>
                  <div>
                    <strong>2,500+</strong>
                    <span>Active Leaders</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SMART COURSE SEARCH BAR & COURSE FINDER Mega-Menu Section */}
        <section
          id="course-finder"
          className="course-finder-section"
          data-aos="fade-up"
          data-aos-duration="800"
        >
          <div className="section-header">
            <h2>Find Your <span className="gradient-text">Ideal Course</span></h2>
            <p className="subtitle">Search, select a category, and explore modern professional modules aligned to your future career goals.</p>
          </div>

          <div className="finder-container">
            {/* SMART SEARCH BAR */}
            <div className="search-bar-wrapper">
              <form onSubmit={handleSearchSubmit} className="search-form">
                <Input
                  placeholder="Search Your Course Here... (e.g. QuickBooks, AI, Power BI)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  prefix={<SearchOutlined className="search-icon" />}
                  size="large"
                  className="search-input"
                  allowClear
                />
                <Button type="primary" size="large" onClick={handleSearchSubmit} className="search-submit-btn">
                  Search
                </Button>
              </form>
              <div className="search-examples">
                <span className="label">Search Examples:</span>
                <span className="tag" onClick={() => handleTagClick("QuickBooks")}>QuickBooks</span>
                <span className="tag" onClick={() => handleTagClick("AI")}>AI</span>
                <span className="tag" onClick={() => handleTagClick("Power BI")}>Power BI</span>
                <span className="tag" onClick={() => handleTagClick("AAT")}>AAT</span>
                <span className="tag" onClick={() => handleTagClick("Logistics")}>Logistics</span>
              </div>
            </div>

            {/* CATEGORIES Mega Menu Grid / Mobile Accordion */}
            {!isMobile ? (
              // DESKTOP SPLIT MEGA MENU
              <div className="desktop-mega-menu">
                <div className="categories-sidebar">
                  {courseCategories.map((cat) => (
                    <div
                      key={cat.id}
                      className={`category-item-link ${activeCategory === cat.id ? "active" : ""}`}
                      onMouseEnter={() => setActiveCategory(cat.id)}
                      onClick={() => setActiveCategory(cat.id)}
                    >
                      <div className="cat-name-icon">
                        {cat.icon}
                        <span>{cat.name}</span>
                      </div>
                      <RightOutlined className="chevron-icon" />
                    </div>
                  ))}
                </div>

                <div className="mega-menu-content-panel">
                  <div className="panel-header">
                    <h3>{currentCategoryObj.name}</h3>
                    <p className="panel-desc">{currentCategoryObj.description}</p>
                  </div>

                  <div className="subcourses-list-grid">
                    {currentCategoryObj.subCourses.map((sub, idx) => (
                      <div key={idx} className="subcourse-card" onClick={() => handleTagClick(sub)}>
                        <CheckCircleOutlined className="subcourse-icon" />
                        <span>{sub}</span>
                      </div>
                    ))}
                  </div>

                  <div className="panel-footer-actions">
                    <div className="related-badge-card">
                      <span className="badge-pill">Premium Curriculum</span>
                      <p>Mapped directly to Sri Lankan corporate specifications and recognized licensing boards.</p>
                    </div>
                    <Button
                      type="primary"
                      className="btn-solid-gold border-radius-8"
                      onClick={() => handleTagClick(currentCategoryObj.name)}
                    >
                      View {currentCategoryObj.name} Courses
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              // MOBILE ACCORDION COURSE MENUS
              <div className="mobile-accordion-list">
                {courseCategories.map((cat) => (
                  <div key={cat.id} className={`accordion-card ${expandedCategory === cat.id ? "expanded" : ""}`}>
                    <div
                      className="accordion-header"
                      onClick={() => setExpandedCategory(expandedCategory === cat.id ? null : cat.id)}
                    >
                      <div className="accordion-title-wrapper">
                        {cat.icon}
                        <span>{cat.name}</span>
                      </div>
                      <span className="accordion-indicator">
                        {expandedCategory === cat.id ? "−" : "+"}
                      </span>
                    </div>

                    {expandedCategory === cat.id && (
                      <div className="accordion-content animate-slide-down">
                        <p className="accordion-desc">{cat.description}</p>
                        <div className="mobile-subcourses-list">
                          {cat.subCourses.map((sub, idx) => (
                            <div key={idx} className="mobile-subcourse-item" onClick={() => handleTagClick(sub)}>
                              <span>{sub}</span>
                              <RightOutlined style={{ fontSize: '10px' }} />
                            </div>
                          ))}
                        </div>
                        <Button
                          type="primary"
                          block
                          className="accordion-cta btn-solid-gold"
                          onClick={() => handleTagClick(cat.name)}
                        >
                          View Courses
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* POPULAR COURSES SECTION */}
        <section
          id="courses-section"
          className="courses"
          data-aos="fade-up"
          data-aos-duration="800"
          data-aos-offset="100"
        >
          <div className="featured-courses">
            <h2>
              Our <span className="gradient-text">Popular Courses</span>
            </h2>
            <p className="para">
              Expand your accounting, finance, logistics, IT, and AI skillset with hands-on professional coaching.
            </p>
            {searchQuery && (
              <div className="search-filter-tag-pill">
                <span>Showing search results for: <strong>"{searchQuery}"</strong></span>
                <Button type="link" size="small" onClick={() => setSearchQuery("")}>Clear Search</Button>
              </div>
            )}
          </div>

          <div className="cources-body">
            <Spin tip="Loading Courses..." spinning={isLoading}>
              {getFilteredCourses().length > 0 ? (
                <List
                  grid={{
                    gutter: 30,
                    xs: 1,
                    sm: 1,
                    md: 2,
                    lg: 2,
                    xl: 3,
                    xxl: 3,
                  }}
                  dataSource={getFilteredCourses()}
                  renderItem={(item) => (
                    <List.Item>
                      <div className="course-card">
                        <div className="card-image-container">
                          <img
                            src={
                              item?.coverImage
                                ? item?.coverImage
                                : DEFAULT_COURSE_COVER_IMAGE
                            }
                            alt={item?.name || "Course Cover"}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=600&h=400&fit=crop";
                            }}
                          />
                          <div className="course-tag-pill">
                            <ClockCircleOutlined style={{ marginRight: "5px" }} />
                            {getCourseDuration(item)}
                          </div>
                        </div>
                        <div className="card-body">
                          <div className="card-top-meta">
                            <div className="course-time-period">
                              <ClockCircleOutlined className="time-icon" />
                              <span className="time-label">Time Period:</span>
                              <span className="time-value">{getCourseDuration(item)}</span>
                            </div>

                            {item?.lecturer && (
                              <div className="author">
                                <img
                                  src={
                                    item?.lecturerProfileImage || item?.createdBy?.profileImage || DEFAULT_USER_PROFILE_IMAGE
                                  }
                                  alt={item?.lecturer}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100&h=100&fit=crop";
                                  }}
                                />
                                <h3 title={item?.lecturer}>
                                  {item?.lecturer}
                                </h3>
                              </div>
                            )}
                          </div>

                          <h3 className="title" title={item?.name}>{item?.name}</h3>

                          <div className="card-pricing-section">
                            {(() => {
                              const priceNum = Number(item?.price);
                              const promoNum = Number(item?.promoPrice);
                              const isFree = item?.price === 0 || item?.price === "Free" || priceNum === 0;
                              const inPromoPeriod = !isFree && isCourseInPromoPeriod(item);

                              if (isFree) {
                                return (
                                  <div className="course-pricing-box free">
                                    <div className="price-item free-item">
                                      <span className="price-tag-label">Course Price:</span>
                                      <span className="free-badge">Free Seminar</span>
                                    </div>
                                  </div>
                                );
                              }

                              if (inPromoPeriod) {
                                const discountDiff = priceNum - promoNum;
                                const discountPercent = Math.round((discountDiff / priceNum) * 100);
                                return (
                                  <div className="course-pricing-box discounted">
                                    <div className="pricing-split-row">
                                      <div className="price-col discounted-col">
                                        <span className="price-tag-label">Discounted Price:</span>
                                        <span className="price-main-val highlight">{formatLKR(promoNum)}</span>
                                      </div>
                                      <div className="price-col original-col">
                                        <span className="price-tag-label">Course Price:</span>
                                        <span className="price-main-val strikethrough"><del>{formatLKR(priceNum)}</del></span>
                                      </div>
                                    </div>
                                    <div className="discount-footer-row">
                                      <span className="discount-save-tag">Save {formatLKR(discountDiff)} ({discountPercent}% OFF)</span>
                                      <PromoCountdown endDate={item.promoEndDate} />
                                    </div>
                                  </div>
                                );
                              }

                              return (
                                <div className="course-pricing-box standard">
                                  <div className="price-col standard-col">
                                    <span className="price-tag-label">Course Price:</span>
                                    <span className="price-main-val primary">{formatLKR(priceNum)}</span>
                                  </div>
                                  {item?.alumniDiscount > 0 && !isNaN(Number(item.alumniDiscount)) && (
                                    <div className="alumni-row">
                                      <span className="alumni-badge">
                                        Alumni Price: {formatLKR(priceNum - Number(item.alumniDiscount))}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>

                        <div className="card-footer">
                          <div className="button-continer">
                            <Link
                              className="view-btn"
                              to={`/register`}
                            >
                              <Button type="primary" style={{ background: '#03245e', borderColor: '#03245e', color: '#fff' }}>Enroll Now</Button>
                            </Link>

                            {item?.whatsappGroupLink ? (
                              <a
                                href={item?.whatsappGroupLink}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <Button
                                  type="primary"
                                  className="whatsapp-btn"
                                >
                                  <WhatsAppOutlined />
                                  Join Whatsapp Group
                                </Button>
                              </a>
                            ) : (
                              <a
                                href={`https://wa.me/94712163163?text=I%20want%20to%20enroll%20in%20${encodeURIComponent(item?.name)}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <Button
                                  type="default"
                                  className="whatsapp-btn"
                                  style={{ background: '#25d366', borderColor: '#25d366', color: '#fff' }}
                                >
                                  <WhatsAppOutlined />
                                  Inquire on WhatsApp
                                </Button>
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              ) : (
                <div className="no-courses-found-box">
                  <p>No courses match your filter keyword <strong>"{searchQuery}"</strong>.</p>
                  <Button type="primary" onClick={() => setSearchQuery("")} className="btn-solid-gold">Reset Course Filter</Button>
                </div>
              )}
            </Spin>
          </div>
        </section>

        {/* WHY CHOOSE IBAS SECTION (8 standard items) */}
        <section id="features-heading" className="courses margin_low">
          <div
            className="features-heading"
            data-aos="fade-up"
            data-aos-duration="800"
            data-aos-offset="100"
          >
            <h2>
              Why Choose <span className="gradient-text">IBAS</span>
            </h2>
            <p className="para">
              Gateway to Professional Business & Accounting Excellence
            </p>
            <p>
              Institute of Business and Accounting Studies (Pvt) Ltd (IBAS) is a government-registered higher education institute (PV No: 00264413) dedicated to bridging the academic-professional gap by compiling specialized, employer-aligned learning structures with flexible modules.
            </p>
          </div>

          <div className="cources-body">
            <List
              grid={{
                gutter: 30,
                xs: 1,
                sm: 1,
                md: 2,
                lg: 3,
                xl: 4,
                xxl: 4,
              }}
              dataSource={specifications}
              renderItem={(item, index) => (
                <List.Item>
                  <div
                    key={item?.title}
                    className="list-card"
                    data-aos="fade-up"
                    data-aos-delay={index * 100}
                    data-aos-once="true"
                  >
                    <div className="icon-wrapper">{item?.icon}</div>
                    <h3>{item?.title}</h3>
                    <div className="description">
                      <p>{item?.description}</p>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </div>
        </section>

        {/* FACULTIES SECTION */}
        <section
          id="faculties"
          className="faculties-section"
          data-aos="fade-up"
          data-aos-duration="800"
        >
          <div className="section-header">
            <h2>Our Highly Distinguished <span className="gradient-text">Academic Panel</span></h2>
            <p className="subtitle">IBAS panel draws together eminent educators, industry leaders, and certified practitioners representing standard committees.</p>
          </div>

          <div className="faculties-grid-container">
            {renderAcademicPanel.map((panel, idx) => (
              <div key={idx} className="faculty-card" data-aos="zoom-in" data-aos-delay={idx * 50}>
                <div className="faculty-emoji-header">
                  {typeof panel.icon === "string" && (panel.icon.startsWith("http") || panel.icon.startsWith("data:") || panel.icon.includes("/")) ? (
                    <img src={panel.icon} alt={panel.title} className="faculty-image-avatar" style={{ width: "50px", height: "50px", borderRadius: "50%", objectFit: "cover" }} />
                  ) : (
                    panel.icon
                  )}
                </div>
                <h3>{panel.title}</h3>
                <p>{panel.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* VIDEO INTRODUCTION SECTION */}
        <section
          className="video-intro-section"
          data-aos="fade-up"
          data-aos-duration="850"
        >
          <div className="video-container-card">
            <div className="video-text-content">
              <h2>Watch Our <span className="gold-text">Campus Introduction</span></h2>
              <p>Take a virtual tour of our modern facilities at Gampaha, see our online webinar setups, meet our Senior Chartered Accounting lectures, and hear real student success stories.</p>
              <div className="bullets-panel">
                <p>✓ Guided physical campus walkthrough</p>
                <p>✓ Preview of the student LMS dashboard</p>
                <p>✓ Sneak-peak into live QuickBooks classes</p>
              </div>
              <Button type="primary" size="large" icon={<PlayCircleOutlined />} onClick={() => setVideoModalVisible(true)} className="play-button-gold">
                Watch Introduction Video
              </Button>
            </div>
            <div className="video-thumbnail-side" onClick={() => setVideoModalVisible(true)}>
              <div className="thumbnail-overlay">
                <PlayCircleOutlined className="giant-play-icon" />
                <span>Click to Play Intro (3:45)</span>
              </div>
            </div>
          </div>
        </section>

        {/* PROFESSIONAL PROGRAMS SECTION */}
        <section
          id="professional-programs"
          className="professional-programs-section"
          data-aos="fade-up"
          data-aos-duration="800"
        >
          <div className="section-header">
            <h2>Professional <span className="gradient-text">Exam Programs</span></h2>
            <p className="subtitle">We prepare students for prestigious local and international corporate examinations with verified pass rates.</p>
          </div>

          <div className="programs-layout-grid">
            <div className="program-badge-box">
              <div className="pro-header">AAT Sri Lanka</div>
              <p>Recognized and accredited by the Association of Accounting Technicians of Sri Lanka. Comprehensive study kits, calculations practices, and mock exams.</p>
              <Link to="/register" className="pro-link">Enroll in AAT Classes →</Link>
            </div>

            <div className="program-badge-box">
              <div className="pro-header">Chartered Accounting (CA)</div>
              <p>Preparatory coaching for local Chartered Accountant exams. Managed entirely by Senior Chartered Accountants (FCA/ACA) with real workplace files.</p>
              <Link to="/register" className="pro-link">Enroll in CA Classes →</Link>
            </div>

            <div className="program-badge-box">
              <div className="pro-header">SLIM Qualifications</div>
              <p>Sri Lanka Institute of Marketing (SLIM) coaching programs for aspiring marketing executives, brand managers, and sales entrepreneurs.</p>
              <Link to="/register" className="pro-link">Enroll in SLIM Classes →</Link>
            </div>
          </div>
        </section>

        {/* STATS SECTION */}
        <section
          className="stats-section"
          data-aos="fade-up"
          data-aos-duration="800"
          data-aos-offset="100"
        >
          <div className="stats-container">
            <div className="stats-grid">
              {statsData.map((s, i) => (
                <div
                  key={i}
                  className="stats-card"
                  data-aos="fade-up"
                  data-aos-delay={i * 100}
                  data-aos-once="true"
                >
                  <div className="stats-icon">{s.icon}</div>
                  <div className="stats-text">
                    <p className="stats-value">{s.value}</p>
                    <p className="stats-label">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS SECTION */}
        <section className="testimonials-section">
          <div
            className="container"
            data-aos="fade-up"
            data-aos-duration="800"
            data-aos-offset="100"
          >
            <h2 className="testimonials-title">
              What Our <span className="gradient-text">Students Say</span>
            </h2>
            <p className="testimonials-subtitle">
              Hear directly from certified graduates who transformed their careers with IBAS Institute.
            </p>

            <div className="testimonial-carousel-wrapper">
              <button
                className="arrow-btn prev-btn"
                onClick={() => carouselRef.current.prev()}
              >
                <LeftOutlined />
              </button>

              <Carousel
                ref={carouselRef}
                autoplay
                dots
                arrows={false}
              >
                {renderTestimonials.map((t) => (
                  <div key={t.id}>
                    <div className="testimonial-card">
                      <div className="rating-stars-wrapper">
                        {[...Array(t.rating)].map((_, i) => (
                          <StarFilled key={i} className="gold-star-icon" />
                        ))}
                      </div>
                      <p className="testimonial-content">"{t.content}"</p>
                      <div className="testimonial-author">
                        <img
                          src={t.image}
                          alt={t.author}
                          className="author-avatar"
                        />
                        <div className="author-info">
                          <h4 className="author-name">{t.author}</h4>
                          <p className="author-role">
                            Completed: <strong>{t.course}</strong>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </Carousel>

              <button
                className="arrow-btn next-btn"
                onClick={() => carouselRef.current.next()}
              >
                <RightOutlined />
              </button>
            </div>
          </div>
        </section>

        {/* BLOG & NEWS SECTION */}
        <section
          id="blog-news"
          className="blog-news-section"
          data-aos="fade-up"
          data-aos-duration="800"
        >
          <div className="section-header">
            <h2>Blog & <span className="gradient-text">Institute News</span></h2>
            <p className="subtitle">Get the latest exam notices, structural updates, accounting guides, and technology integration trends.</p>
          </div>

          <div className="blog-posts-grid">
            <div className="blog-card" data-aos="fade-up" data-aos-delay="0">
              <div className="blog-img-wrapper">
                <img src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&h=400&fit=crop" alt="AAT Sri Lanka Exam Prep Tips" />
              </div>
              <div className="blog-body">
                <span className="blog-date">May 12, 2026 • Academic Notices</span>
                <h3>Top Tips to Clear AAT Level III Calculation Papers</h3>
                <p>Calculations in AAT can be daunting. Our Senior Chartered Accounting lecturers share strategies to score full marks in audit equations...</p>
                <Link to="/register" className="blog-read-more">Read Full Article →</Link>
              </div>
            </div>

            <div className="blog-card" data-aos="fade-up" data-aos-delay="100">
              <div className="blog-img-wrapper">
                <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop" alt="Power BI dashboards in accounting" />
              </div>
              <div className="blog-body">
                <span className="blog-date">April 28, 2026 • Technology Trends</span>
                <h3>Why Every Modern Accountant Needs Power BI Skills</h3>
                <p>Simple static balance sheets are no longer enough for boardrooms. Visual dashboard reporting is the new global standard for corporate analysts...</p>
                <Link to="/register" className="blog-read-more">Read Full Article →</Link>
              </div>
            </div>

            <div className="blog-card" data-aos="fade-up" data-aos-delay="200">
              <div className="blog-img-wrapper">
                <img src="https://images.unsplash.com/photo-1677442136019-21780efad99a?w=600&h=400&fit=crop" alt="AI in Sri Lankan Business" />
              </div>
              <div className="blog-body">
                <span className="blog-date">March 15, 2026 • Skill Development</span>
                <h3>Integrating Artificial Intelligence into SME Audits</h3>
                <p>AI prompt engineering tools are automating spreadsheet structures. See how local companies use ChatGPT to audit transaction logs in minutes...</p>
                <Link to="/register" className="blog-read-more">Read Full Article →</Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA SECTION */}
        <section className="cta-section">
          <div
            className="section-container"
            data-aos="fade-up"
            data-aos-duration="800"
            data-aos-offset="100"
          >
            <div className="cta-content">
              <div className="cta-image-wrapper">
                <img src={cta} alt="Start learning today at IBAS" />
              </div>
              <div className="cta-text-wrapper">
                <h2 className="cta-title">
                  Ready to <span className="gradient-text">Advance Your Career?</span>
                </h2>
                <p className="cta-description">
                  Join thousands of working professionals and students who have already qualified their accredited accounting certifications and practical IT capabilities with IBAS.
                </p>
                <ul className="cta-features-list">
                  {features.map((feat, idx) => (
                    <li key={idx}>
                      <svg
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        className="cta-icon"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
                <div className="cta-buttons">
                  <Link className="cta-button animate-pulse" to="/register">
                    Register Now
                  </Link>
                  <button className="secondary-button" onClick={() => scrollToSection("course-finder")}>
                    Browse Categories
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CONTACT US PAGE SECTION */}
        <section
          id="contact-us"
          className="contact-us-section"
          data-aos="fade-up"
          data-aos-duration="850"
        >
          <div className="section-header">
            <h2>Contact <span className="gradient-text">IBAS Institute</span></h2>
            <p className="subtitle">Have inquiries about registration, fees, or class schedules? Send us a message or visit our Gampaha Head Office.</p>
          </div>

          <div className="contact-grid-container">
            {/* INQUIRY FORM */}
            <div className="inquiry-form-card">
              <h3>Submit an Inquiry</h3>
              <p>Complete the form below and an enrollment officer will respond within 24 hours.</p>
              <form onSubmit={handleInquirySubmit} className="inquiry-form-fields">
                <div className="form-group">
                  <label htmlFor="name">Full Name <span className="required">*</span></label>
                  <Input
                    id="name"
                    required
                    placeholder="Enter your name"
                    value={inquiryName}
                    onChange={(e) => setInquiryName(e.target.value)}
                    size="large"
                  />
                </div>
                <div className="form-group-row">
                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="name@example.com"
                      value={inquiryEmail}
                      onChange={(e) => setInquiryEmail(e.target.value)}
                      size="large"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Mobile Number <span className="required">*</span></label>
                    <Input
                      id="phone"
                      required
                      placeholder="e.g. 0712163163"
                      value={inquiryPhone}
                      onChange={(e) => setInquiryPhone(e.target.value)}
                      size="large"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="course">Course of Interest</label>
                  <select
                    id="course"
                    className="custom-form-select"
                    value={inquiryCourse}
                    onChange={(e) => setInquiryCourse(e.target.value)}
                  >
                    <option value="">-- Select Course --</option>
                    <option value="QuickBooks Accounting">QuickBooks Accounting</option>
                    <option value="Advanced Excel & Power BI">Advanced Excel & Power BI</option>
                    <option value="AI for Business & ChatGPT">AI for Business & ChatGPT</option>
                    <option value="AAT exam prep classes">AAT exam prep classes</option>
                    <option value="Logistics & Supply Chain Management">Logistics & Supply Chain Management</option>
                    <option value="Business Management Diploma">Business Management Diploma</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="message">Your Message <span className="required">*</span></label>
                  <Input.TextArea
                    id="message"
                    required
                    placeholder="Enter details of your inquiry..."
                    value={inquiryMessage}
                    onChange={(e) => setInquiryMessage(e.target.value)}
                    rows={4}
                  />
                </div>
                <Button type="primary" htmlType="submit" size="large" className="submit-btn-gold" loading={isInquirySubmitting}>
                  Submit Inquiry
                </Button>
              </form>
            </div>

            {/* CONTACT DETAILS & GOOGLE MAPS */}
            <div className="contact-info-panel-side">
              <div className="info-card">
                <h3>Office Details</h3>

                <div className="info-row">
                  <EnvironmentTwoTone className="info-icon" twoToneColor="#f0b323" />
                  <div>
                    <h4>Head Office</h4>
                    <p>No. 04, Bauddhaloka Mawatha, Gampaha, Sri Lanka</p>
                  </div>
                </div>

                <div className="info-row">
                  <PhoneTwoTone className="info-icon" twoToneColor="#f0b323" />
                  <div>
                    <h4>Hotlines</h4>
                    <p>071 216 3163 | 033 232 1966</p>
                  </div>
                </div>

                <div className="info-row">
                  <MailTwoTone className="info-icon" twoToneColor="#f0b323" />
                  <div>
                    <h4>Email Address</h4>
                    <p><a href="mailto:ibasinstitute@gmail.com">ibasinstitute@gmail.com</a></p>
                  </div>
                </div>

                <div className="whatsapp-direct-box">
                  <p>Prefer instant messaging? Chat directly with an enrollment counselor on WhatsApp.</p>
                  <a href="https://wa.me/94712163163" target="_blank" rel="noreferrer" className="whatsapp-big-btn">
                    <WhatsAppOutlined /> Message Us on WhatsApp
                  </a>
                </div>
              </div>

              {/* GOOGLE MAPS EMBED */}
              <div className="google-map-container">
                <iframe
                  title="IBAS Google Map Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3959.0494488349247!2d79.99596327475855!3d7.097063492906325!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae2fb87b9264fa7%3A0xe21262d083e913a!2sBauddhaloka%20Mawatha%2C%20Gampaha%2011000%2C%20Sri%20Lanka!5e0!3m2!1sen!2sua!4v1716124500000!5m2!1sen!2sua"
                  width="100%"
                  height="250"
                  style={{ border: 0, borderRadius: '12px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>
        </section>
      </div>

      <footer id="footer">
        <div className="footer-sec">
          <div className="logo">
            <a href="/">
              <img src={logo} alt="IBAS logo" />
            </a>
          </div>
          <div className="text">
            <p>
              Institute of Business and Accounting Studies (Pvt) Ltd (IBAS) is a government-registered higher education institute (PV No: 00264413), recognized and accredited by AAT Sri Lanka. We develop industry-ready leaders via premium interactive accounting, management, and technology systems.
            </p>
          </div>
          <div className="social-links-row">
            <a href="https://facebook.com" target="_blank" rel="noreferrer" className="social-circle"><FacebookOutlined /></a>
            <a href="https://linkedin.com" target="_blank" rel="noreferrer" className="social-circle"><LinkedinOutlined /></a>
            <a href="https://twitter.com" target="_blank" rel="noreferrer" className="social-circle"><TwitterOutlined /></a>
            <a href="https://youtube.com" target="_blank" rel="noreferrer" className="social-circle"><YoutubeOutlined /></a>
          </div>
        </div>

        <div className="footer-sec">
          <h3>Quick Links</h3>
          <div className="text footer-links">
            <p>
              <Button type="link" onClick={() => scrollToSection("landinghome")}>
                Home
              </Button>
            </p>
            <p>
              <Link to="/verify-certificate">
                <Button type="link">Verify Certificate</Button>
              </Link>
            </p>
            <p>
              <Button type="link" onClick={() => scrollToSection("course-finder")}>
                Course Finder mega-menu
              </Button>
            </p>
            <p>
              <Button type="link" onClick={() => scrollToSection("courses-section")}>
                Popular Courses
              </Button>
            </p>
            <p>
              <Button type="link" onClick={() => scrollToSection("faculties")}>
                distinguished Faculty
              </Button>
            </p>
            <p>
              <Button type="link" onClick={() => scrollToSection("professional-programs")}>
                Accredited Exam prep
              </Button>
            </p>
            <p>
              <Link to="/login">
                <Button type="link">LMS Login Portal</Button>
              </Link>
            </p>
          </div>
        </div>

        <div className="footer-sec">
          <h3>Gampaha Head Office</h3>
          <div className="text text-contacts">
            <p>
              <span>
                <EnvironmentTwoTone twoToneColor="#f0b323" />
              </span>
              No. 04, Bauddhaloka Mawatha, Gampaha, Sri Lanka.
            </p>
            <p>
              <span>
                <PhoneTwoTone twoToneColor="#f0b323" />
              </span>
              071 216 3163 | 033 232 1966
            </p>
            <p>
              <span>
                <MailTwoTone twoToneColor="#f0b323" />
              </span>
              <a href="mailto:ibasinstitute@gmail.com">ibasinstitute@gmail.com</a>
            </p>
          </div>
        </div>
      </footer>

      <section className="copy-right">
        <p>
          © 2026
          <a href="http://ibas.lk/" target="_blank" rel="noreferrer">
            {" "}
            IBAS.
          </a>{" "}
          All Rights Reserved.
        </p>

        <p>
          Designed & Developed by
          <a href="https://lionbit.cc/" target="_blank" rel="noreferrer">
            {" "}
            LionBit Solutions.
          </a>
        </p>
      </section>

      {/* VIDEO PREVIEW MODAL */}
      <Modal
        title="IBAS Institute - Campus Introduction"
        open={videoModalVisible}
        onCancel={() => setVideoModalVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <div className="video-player-container">
          <iframe
            width="100%"
            height="450"
            src="https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1"
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      </Modal>
    </div>
  );
};

export default Landing;
